import type {ReactElement} from 'react';
import React from 'react';
import {BaseTextOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-text-outline-option';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromTextStylesOfSelectedTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesOfSelectedTextSlideItem';
import {useGetKeyFromSelectedTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromSelectedTextSlideItem';

interface TextSlideOutlineOptionProps {
  onClickColorFill(): void;
}

export function TextSlideOutlineOption({...props}: TextSlideOutlineOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const stroke = useGetKeyFromTextStylesOfSelectedTextSlideItem('stroke');
  const isBold = useGetKeyFromTextStylesOfSelectedTextSlideItem('isBold');
  const strokeWidth = useGetKeyFromTextStylesOfSelectedTextSlideItem('strokeWidth');
  const strokeColor = useGetKeyFromTextStylesOfSelectedTextSlideItem('strokeColor');
  const scaleX = useGetKeyFromSelectedTextSlideItem('scaleX');

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item but got ${activeItem?.gitype}`);
  }

  if (!activeSlide?.isTextSlide()) {
    throw new Error(`Expected text slideshow item but got ${activeSlide?.gitype}`);
  }

  if (!activeSlideUID) {
    throw new Error('unable to get the selected text slide');
  }

  if (!strokeColor || strokeWidth === undefined || isBold === undefined || stroke === undefined || scaleX === undefined) {
    throw new Error('unable to get outline properties for text slide');
  }

  const onClickColorFill = (): void => {
    props.onClickColorFill();
  };

  const onToggleOutlineOption = (): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      textStyles: {
        stroke: !stroke,
        strokeWidth: activeSlide.textStyles.getDefaultStrokeWidth(ITEM_TYPE.TEXTSLIDE, scaleX),
        strokeColor: activeSlide.textStyles.getColorForStroke(),
      },
    });
  };

  const updateStrokeThickness = (strokeWidth: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        textStyles: {
          strokeWidth,
        },
      },
      {undoable}
    );
  };

  return (
    <BaseTextOutlineOption
      stroke={stroke}
      isDisabled={isBold}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      updateStrokeThickness={updateStrokeThickness}
      onToggleOutlineOption={onToggleOutlineOption}
      onClickColorFill={onClickColorFill}
    />
  );
}
